import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DraggedDocument {
  id: string;
  title: string;
}

interface DocumentDragContextType {
  draggedDoc: DraggedDocument | null;
  isDragging: boolean;
  startDrag: (doc: DraggedDocument) => void;
  endDrag: () => void;
  dropOnFolder: (folderId: string, folderName: string) => Promise<void>;
}

const DocumentDragContext = createContext<DocumentDragContextType | null>(null);

export function DocumentDragProvider({ children }: { children: ReactNode }) {
  const [draggedDoc, setDraggedDoc] = useState<DraggedDocument | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const startDrag = useCallback((doc: DraggedDocument) => {
    setDraggedDoc(doc);
    setIsDragging(true);
  }, []);

  const endDrag = useCallback(() => {
    setDraggedDoc(null);
    setIsDragging(false);
  }, []);

  const dropOnFolder = useCallback(async (folderId: string, folderName: string) => {
    if (!draggedDoc) return;

    const { error } = await supabase
      .from("documents")
      .update({ folder_id: folderId })
      .eq("id", draggedDoc.id);

    if (error) {
      toast.error("Erreur lors du déplacement");
      console.error("Move error:", error);
    } else {
      toast.success(`"${draggedDoc.title}" déplacé vers "${folderName}"`);
    }

    endDrag();
  }, [draggedDoc, endDrag]);

  return (
    <DocumentDragContext.Provider value={{ draggedDoc, isDragging, startDrag, endDrag, dropOnFolder }}>
      {children}
    </DocumentDragContext.Provider>
  );
}

export function useDocumentDrag() {
  const context = useContext(DocumentDragContext);
  if (!context) {
    throw new Error("useDocumentDrag must be used within a DocumentDragProvider");
  }
  return context;
}
